import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverIndigenousHeritage() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-indigenous-heritage";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles
    ? articles.find((art) => art.slug === ref_id)
    : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "location-1": "Brockton Point Totem Poles",
    "location-2": "Siwash Rock",
    "location-3": "Museum of Anthropology (MOA)",
    "location-4": "Musqueam Cultural Centre",
    "location-5": "Bill Reid Gallery",
    "location-6": "Whey-ah-Wichen (Cates Park)",
    "location-7": "Skwachàys Lodge",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    references: "References",
    conclusion: "Conclusion",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/stanley-park.aspx",
      label: "City of Vancouver: Stanley Park",
    },
    {
      url: "https://indigenousbc.com/",
      label: "Indigenous Tourism BC",
    },
    {
      url: "https://www.musqueam.bc.ca/",
      label: "Musqueam Indian Band",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match
  // the ref you use in the corresponding <p> or <li> so scrolling + markers line up.
  const locations = [
    {
      id: "location-1",
      lat: 49.29929,
      lng: -123.12103,
      popupText: "Brockton Point Totem Poles, Stanley Park",
    },
    {
      id: "location-2",
      lat: 49.30972,
      lng: -123.15600,
      popupText: "Siwash Rock, Stanley Park",
    },
    {
      id: "location-3",
      lat: 49.26937,
      lng: -123.25960,
      popupText: "Museum of Anthropology (MOA) at UBC",
    },
    {
      id: "location-4",
      lat: 49.22388,
      lng: -123.19905,
      popupText: "Musqueam Cultural Centre",
    },
    {
      id: "location-5",
      lat: 49.28476,
      lng: -123.11948,
      popupText: "Bill Reid Gallery of Northwest Coast Art",
    },
    {
      id: "location-6",
      lat: 49.30321,
      lng: -122.95529,
      popupText: "Whey-ah-Wichen (Cates Park)",
    },
    {
      id: "location-7",
      lat: 49.28098,
      lng: -123.10613,
      popupText: "Skwachàys Lodge Indigenous Art Hotel",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver Indigenous Heritage: Must-Visit Places & Practical Tips</title>
        <meta
          name="description"
          content="Explore Vancouver's top Indigenous heritage sites, including Stanley Park Totem Poles, MOA at UBC, and more. Learn respectful visitation tips, FAQs, and local guidelines."
        />
        <meta
          name="keywords"
          content="Vancouver, Indigenous, Heritage, Totem Poles, MOA, Museum, Culture, Travel Tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-indigenous-heritage.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver Indigenous Heritage: Must-Visit Places & Practical Tips"
        />
        <meta
          property="og:description"
          content="Discover Vancouver’s Indigenous heritage at iconic sites like Brockton Point Totem Poles and MOA. Get practical visitation tips, FAQs, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-indigenous-heritage.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-indigenous-heritage.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver Indigenous Heritage: Must-Visit Places & Practical Tips"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver’s Indigenous heritage at iconic sites like Brockton Point Totem Poles and MOA. Get practical visitation tips, FAQs, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-indigenous-heritage.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt={
                ref_data?.["image-desc"] ||
                "Indigenous Heritage Sites in Vancouver"
              }
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s identity is deeply rooted in the lands and waters of
              the Musqueam, Squamish, and Tsleil-Waututh Nations. These
              territories have witnessed centuries of tradition, storytelling,
              and art passed down through generations. Today, the city hosts a
              thriving Indigenous community and numerous heritage sites where
              visitors can connect with local cultures. This guide highlights
              Vancouver’s must-visit Indigenous heritage places, offering
              practical tips, addresses, approximate costs, and more. Explore
              respectfully and enrich your travels with authentic cultural
              insights.
            </p>

            {/* Location 1: Brockton Point Totem Poles */}
            <h2 id="location-1">Brockton Point Totem Poles, Stanley Park</h2>
            <p
              ref={(el) => setSpotRef("location-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Brockton Point Totem Poles</strong>{" "}
              <Marker
                coordY={49.29929}
                coordX={-123.12103}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              are one of Vancouver’s most iconic attractions. Located at the
              eastern tip of Stanley Park, these towering cedar poles represent
              the art and stories of several First Nations from the Pacific
              Northwest. There are nine poles in total, each depicting
              mythological beings and clan crests. The site is free to visit, is
              open year-round, and offers interpretive plaques to help visitors
              learn the poles’ histories. 
            </p>
            <p>
              <strong>Highlights & Tips:</strong> Early morning visits provide a
              quieter atmosphere. Parking is available nearby (paid). The area
              is wheelchair accessible with paved paths. Please avoid touching
              or climbing on the totem poles to preserve their cultural and
              artistic integrity.
            </p>

            {/* Location 2: Siwash Rock */}
            <h2 id="location-2">Siwash Rock, Stanley Park</h2>
            <p
              ref={(el) => setSpotRef("location-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Siwash Rock</strong>{" "}
              <Marker
                coordY={49.30972}
                coordX={-123.15600}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is a striking sea stack on the western side of Stanley Park. Known
              in the Squamish language as <em>Slhx̱i7lsh</em>, it’s tied to
              legends of a man transformed into stone. This sacred site
              symbolizes ancient values and the Coast Salish spiritual
              connection to the land. You can view Siwash Rock up close from the
              Stanley Park Seawall; it’s about a half-hour walk from Brockton
              Point.
            </p>
            <p>
              <strong>Highlights & Tips:</strong> The seawall is paved and
              accessible, though it’s a longer walk if mobility is an issue.
              Don’t climb or attempt to scale the rock, as it’s both dangerous
              and disrespectful to local cultural traditions.
            </p>

            {/* Location 3: Museum of Anthropology */}
            <h2 id="location-3">Museum of Anthropology (MOA) at UBC</h2>
            <p
              ref={(el) => setSpotRef("location-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Museum of Anthropology</strong>{" "}
              <Marker
                coordY={49.26937}
                coordX={-123.25960}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is located at the University of British Columbia, featuring an
              expansive collection of Indigenous art and artifacts. From massive
              totem poles to intricate carvings, MOA showcases the creativity
              and history of First Nations across the Pacific Northwest. Expect
              to spend 2–3 hours exploring the Great Hall, indoor galleries, and
              outdoor exhibits.
            </p>
            <p>
              <strong>Highlights & Tips:</strong> Admission is around CAD $18
              for adults, with discounts available. Guided tours are often
              included. The site is fully wheelchair accessible. Check their
              website for current hours and any closures. The famous Bill Reid
              sculpture <em>The Raven and the First Men</em> is a must-see.
            </p>

            {/* Location 4: Musqueam Cultural Centre */}
            <h2 id="location-4">Musqueam Cultural Centre and Museum</h2>
            <p
              ref={(el) => setSpotRef("location-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Musqueam Cultural Centre</strong>{" "}
              <Marker
                coordY={49.22388}
                coordX={-123.19905}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              offers a community-centered perspective on Musqueam history,
              language, and art. Located in South Vancouver, it’s operated by
              the Musqueam Indian Band and showcases artifacts, contemporary
              artwork, and educational exhibits about the ancient village sites
              in this area.
            </p>
            <p>
              <strong>Highlights & Tips:</strong> The centre is typically open
              weekdays with a small admission fee. Call ahead or check their
              website to confirm hours. Be mindful that you’re visiting a living
              community space—respect signage and privacy as you explore.
            </p>

            {/* Location 5: Bill Reid Gallery */}
            <h2 id="location-5">Bill Reid Gallery of Northwest Coast Art</h2>
            <p
              ref={(el) => setSpotRef("location-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Bill Reid Gallery</strong>{" "}
              <Marker
                coordY={49.28476}
                coordX={-123.11948}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is nestled in downtown Vancouver, featuring contemporary and
              traditional art from the Northwest Coast. Named after the
              legendary Haida artist Bill Reid, it’s a smaller gallery but packs
              a big cultural punch with regularly rotating exhibits and a
              permanent display of Reid’s works.
            </p>
            <p>
              <strong>Highlights & Tips:</strong> Admission is around CAD $13,
              open Wednesday through Sunday. The gift shop is an excellent place
              to purchase authentic Indigenous art. Allow about an hour for a
              thorough visit.
            </p>

            {/* Location 6: Whey-ah-Wichen (Cates Park) */}
            <h2 id="location-6">Whey-ah-Wichen (Cates Park)</h2>
            <p
              ref={(el) => setSpotRef("location-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Whey-ah-Wichen</strong>{" "}
              <Marker
                coordY={49.30321}
                coordX={-122.95529}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              also known as Cates Park, sits on Tsleil-Waututh territory in
              North Vancouver. Its name means “face the wind,” and it has long
              been an important gathering place. Visitors can explore the
              waterfront trails, spot traditional canoe sheds, or join guided
              paddling tours to learn about local legends and stewardship
              efforts.
            </p>
            <p>
              <strong>Highlights & Tips:</strong> The park is free, family
              friendly, and open year-round. There’s a picnic area, playground,
              and often a concession stand in summer. If you opt for a Tsleil-
              Waututh-led canoe tour, book ahead, especially in peak season.
            </p>

            {/* Location 7: Skwachàys Lodge */}
            <h2 id="location-7">Skwachàys Lodge Indigenous Art Hotel</h2>
            <p
              ref={(el) => setSpotRef("location-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Skwachàys Lodge</strong>{" "}
              <Marker
                coordY={49.28098}
                coordX={-123.10613}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is an Indigenous-owned boutique hotel and gallery located near
              Gastown. Each room features unique Indigenous art and design. Even
              if you don’t stay overnight, you can visit the street-level
              gallery to shop for fair-trade Indigenous artworks. Proceeds
              support an artist-in-residence program, providing affordable
              housing and studio space for Indigenous creatives.
            </p>
            <p>
              <strong>Highlights & Tips:</strong> The on-site gallery is free to
              enter. Because it’s a smaller hotel, rooms book up quickly. The
              neighborhood is busy urban Vancouver; normal city-savvy precautions
              apply.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              Visiting these sites entails respecting Indigenous protocols and
              park rules. Some key practices:
            </p>
            <ul>
              <li>
                **Stay on marked trails** to avoid damaging sensitive
                archaeological or ecological areas.
              </li>
              <li>
                **Do not remove artifacts or disturb sites**. This includes
                shells, carvings, or anything of cultural significance.
              </li>
              <li>
                **Follow photography guidelines**—some exhibits or sacred items
                prohibit pictures. Always ask permission before photographing
                individuals or ceremonies.
              </li>
              <li>
                **Use official parking and obey local bylaws**; Stanley Park,
                for instance, has specific zones for vehicles.
              </li>
              <li>
                **Support Indigenous-owned businesses**—this fosters cultural
                exchange and benefits local communities.
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a guide or special permission to visit?</h3>
              <p>
                Most sites, like Stanley Park’s Totem Poles or Bill Reid
                Gallery, are publicly accessible. No special permission is
                required. However, guided tours (especially Indigenous-led) can
                greatly enhance your experience.
              </p>

              <h3>2. What are the typical costs?</h3>
              <p>
                Many outdoor sites like Stanley Park and Cates Park are free.
                Museums and galleries (MOA, Bill Reid Gallery) have admission
                fees ranging from CAD $13–$18. The Musqueam Cultural Centre
                charges around $5. Skwachàys Lodge gallery is free to visit
                (hotel rates apply if you book a stay).
              </p>

              <h3>3. Are these sites accessible by public transport?</h3>
              <p>
                Yes. Buses run to Stanley Park, UBC (for MOA), and near Gastown.
                Cates Park requires a bus transfer after the Seabus to North Van.
                Musqueam Cultural Centre is reachable by bus with a short walk.
                Check transit schedules in advance.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s Indigenous heritage sites offer a profound look into
              the city’s ancient and living cultures. Whether you’re admiring
              centuries-old totem poles in Stanley Park or discovering
              contemporary Indigenous art at Skwachàys Lodge, these experiences
              enrich one’s understanding of local history and traditions. By
              following best practices, respecting protocols, and engaging with
              community-led experiences, visitors become part of a journey
              toward reconciliation and cross-cultural appreciation. Enjoy your
              exploration, and may it foster deeper connections with Vancouver’s
              First Peoples and the land they have stewarded for millennia.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}